import { useMemo } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { useServices } from "@/services-context";
import styles from "./cart-page.mobile.module.scss";
import { FixAlert } from "@/elements-react/fix-alert";
import CartEmptyLabel from "@/elements-react/cart-empty-label";
import CartItem from "@/elements-react/cart-item";
import PriceValue from "@/elements-react/price-value";
import { FixButton } from "@/elements-react/fix-button";
import { FixDivider } from "@/elements-react/fix-divider";
import { CART_LISTENER_NAME } from "@/context/cart.ts";
import { PARAMETERS_LISTENER_NAME } from "@/services/parameters.ts";
import { googleEvent } from "@/helpers/google-events.ts";
import { useCartCommons } from "./cart-commons";
import { DocumentElements } from "@/elements-react/document-elements/document-elements";
import { LayoutStyleTypes, LayoutTypes } from "@/util-react/layout.tsx";
import {Header} from "@/elements-react/catalog-layout/header";

export default function CartPageMobile() {
  const { t } = useTranslation();
  const services = useServices([CART_LISTENER_NAME, PARAMETERS_LISTENER_NAME]);
  const { cart } = services;

  const availableProducts = useMemo(() => {
    const availableItems = cart?.items?.filter((item) => !item.isDisabled && !item.baseCartProduct) ?? [];
    return availableItems.map((item) => {
      const additionalItem = cart?.items?.find((i) => i.baseCartProduct && i.baseCartProduct.id === item.id);
      return { baseItem: item, additionalItem };
    });
  }, [cart?.items]);

  const disabledProducts = useMemo(() => cart?.items?.filter((item) => item.isDisabled) ?? [], [cart?.items]);

  const { goToCheckout } = useCartCommons({});

  return (
    <div>
      <DocumentElements />
      <div className={styles.root}>
        <Header simple={true} />
        {cart.items?.length ? (
          <div className={styles["cart-container"]}>
            <div className={styles["cart-items-container"]}>
              <div className={styles.alerts}>
                <FixAlert />
              </div>
              <div className={styles["cart-items-total"]}>
                <h1 className={styles.label}>{t("p90ev5c5p8c5048s.shopping-cart", "Shopping cart")}</h1>
              </div>
              <div className={styles.products}>
                {availableProducts.map((item, key) =>
                  item.additionalItem ? (
                    <div className={styles["items-join"]} key={key}>
                      <CartItem product={item.baseItem} singleProduct={false} layout={LayoutStyleTypes.TYPE_SM} />
                      <FixDivider />
                      <CartItem product={item.additionalItem} singleProduct={false} layout={LayoutStyleTypes.TYPE_SM} />
                    </div>
                  ) : (
                    <CartItem product={item.baseItem} key={key} layout={LayoutStyleTypes.TYPE_SM} />
                  ),
                )}
                {disabledProducts.length > 0 && (
                  <div className={styles["disabled-products-divide"]}>
                    {t("wcvjoidfu38gd.disabled-products-label", "Sorry, but these products are no longer available for purchase")}
                  </div>
                )}
                {disabledProducts.map((item, key) => (
                  <div key={key}>
                    {key > 0 && <FixDivider />}
                    <CartItem product={item} layout={LayoutStyleTypes.TYPE_SM} />
                  </div>
                ))}
              </div>
            </div>
            <div className={styles["cart-totals"]}>
              <div className={styles.subtotal}>
                <span>{t("sor9ad5qli79zyjd.subtotal", "Subtotal:")}</span>
                <PriceValue amount={Number(cart.grandTotal?.amount)} />
                <span className={styles["tax-info"]}>
                  ({t("9hkouimd77ty1706.vat-include", "Include {{percent}}% VAT", { percent: cart.taxAdditionalInfo })})
                </span>
              </div>
              <FixButton
                className={styles["checkout-btn"]}
                size="large"
                variant={"primary"}
                onClick={goToCheckout}
                router-ignore="true"
              >
                {t("841teeu1p1a02wyl.to-checkout", "Go to checkout")}
              </FixButton>
              <FixButton
                className={styles["back-btn"]}
                size="large"
                href="/"
                variant={"default"}
                onClick={() => googleEvent("continue_shopping_click")}
              >
                {t("e83geotgxa5m02vb.back-to-shopping", "Back to shopping")}
              </FixButton>
            </div>
          </div>
        ) : (
          <CartEmptyLabel />
        )}
      </div>
    </div>
  );
}
